import React, { ReactElement, useState } from "react";
import styles from "./planDetailCarousel.module.scss";
import { Swiper, SwiperSlide } from "swiper/react";
import { VideoOverlayTrigger } from "../../videoOverlayTrigger";
import { ImageOverlayTrigger } from "../../imageOverlayTrigger";
import dynamic from "next/dynamic";
import { Carousel } from "@kunduz/ui-molecules";
import { ChevronLeft, ChevronRight } from "@untitled-ui/icons-react";

const WistiaPlayer = dynamic(
  () => import("@wistia/wistia-player-react").then((mod) => mod.WistiaPlayer),
  {
    ssr: false,
    loading() {
      return (
        <div style={{ width: "100%", aspectRatio: "16/9" }}>Loading...</div>
      );
    },
  },
);

export const PlanDetailSwiperCarousel = (props: Props): ReactElement => {
  return (
    <div className={styles.carouselWrapper}>
      <Swiper
        freeMode
        spaceBetween={16}
        slidesPerView={"auto"}
        loop={false}
        threshold={10}
        mousewheel={true}
        cssMode
      >
        {props.items?.map((item, i) => (
          <SwiperSlide key={`slide_${i}`} style={{ width: props.width || 310 }}>
            {item.kind === "video" ? (
              <VideoTestimonialCard
                key={`card${i}`}
                {...item}
                width={props.width}
                height={props.height}
              />
            ) : (
              <ImageTestimonialCard
                key={`card${i}`}
                {...item}
                width={props.width}
                height={props.height}
              />
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export const PlanDetailPlainCarousel = (props: Props): ReactElement => {
  const wistas = props.items.filter((item) => item.wistiaId);
  const otherSources = props.items.filter((item) => !item.wistiaId);

  const [initialSlide, setInitialSlide] = useState(0);

  return (
    <div className={styles.carouselWrapper}>
      <div className={styles.plainCarouselOutsideWrapper}>
        <div className={styles.plainCarouselInsideWrapper}>
          {wistas?.map((wista) => (
            <div
              style={{ width: props.width, height: props.height }}
              key={wista.wistiaId}
            >
              <WistiaPlayer
                width={props.width}
                height={props.height}
                mediaId={wista.wistiaId}
                aspect={16 / 9}
                controlsVisibleOnLoad={false}
                roundedPlayer={8}
                volumeControl={false}
                fullscreenControl={false}
                wistiaPopover
                onPlay={() => props.onClick && props.onClick(wista.id, "video")}
              />
            </div>
          ))}

          <Carousel.Root>
            <Carousel.Overlay />
            <Carousel.Trigger className={styles.triggerContainer} asChild>
              <div className={styles.swipeContainer}>
                {otherSources?.map((source, i) => (
                  <CarouselTrigger
                    key={`key_${i}`}
                    height={props.height}
                    index={i}
                    setInitialSlide={setInitialSlide}
                    source={source}
                    width={props.width}
                  />
                ))}
              </div>
            </Carousel.Trigger>
            <Carousel.Content>
              <Carousel.CarouselInstance
                color="gray"
                size="md"
                variant="light"
                type="dot"
                withFrame
                swiperOptions={{ initialSlide, loop: true, keyboard: true }}
                className={styles.carouselInstanceContainer}
              >
                {otherSources?.map((source, i) => (
                  <Carousel.Slide key={i}>
                    <CarouselSlide source={source} />
                  </Carousel.Slide>
                ))}
                <Carousel.PrevButton
                  onClick={() => {}}
                  size="md"
                  variant="light"
                  color="gray"
                  className={styles.carouselButton}
                >
                  <ChevronLeft />
                </Carousel.PrevButton>

                <Carousel.NextButton
                  onClick={() => {}}
                  size="md"
                  variant="light"
                  color="gray"
                  className={styles.carouselButton}
                >
                  <ChevronRight />
                </Carousel.NextButton>
              </Carousel.CarouselInstance>
            </Carousel.Content>
          </Carousel.Root>
        </div>
      </div>
    </div>
  );
};

const VideoTestimonialCard = (
  props: Props["items"][0] & {
    width: Props["width"];
    height: Props["height"];
    onClick?: () => void;
  },
): ReactElement => {
  return (
    <VideoOverlayTrigger
      triggerNode={
        <div
          className={styles.cardWrapper}
          style={{ width: props.width, height: props.height }}
        >
          <div
            className={styles.cardContent}
            style={{
              backgroundImage: `url(${props.thumbnail})`,
            }}
          >
            <PlayButton className={styles.playButton} />
          </div>
        </div>
      }
      mediaFile={props.media}
      onClick={props.onClick}
    />
  );
};

const ImageTestimonialCard = (
  props: Props["items"][0] & {
    width: Props["width"];
    height: Props["height"];
    onClick?: () => void;
  },
): ReactElement => {
  return (
    <ImageOverlayTrigger
      triggerNode={
        <div
          className={styles.cardWrapper}
          style={{ width: props.width, height: props.height }}
        >
          <div
            className={styles.cardContent}
            style={{
              backgroundImage: `url(${props.thumbnail})`,
            }}
          ></div>
        </div>
      }
      mediaFile={props.media}
      onClick={props.onClick}
    />
  );
};

const PlayButton = (props: any) => {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24 48C37.2548 48 48 37.2548 48 24C48 10.7452 37.2548 0 24 0C10.7452 0 0 10.7452 0 24C0 37.2548 10.7452 48 24 48ZM20.25 32.8033L33.75 25.2576C34.75 24.6987 34.75 23.3013 33.75 22.7424L20.25 15.1967C19.25 14.6378 18 15.3365 18 16.4543V31.5457C18 32.6635 19.25 33.3622 20.25 32.8033Z"
        fill="white"
      />
    </svg>
  );
};

const CarouselTrigger = (props: {
  source: {
    kind: "image" | "video";
    thumbnail: string;
    media: string;
    wistiaId?: string;
    id?: any;
  };
  setInitialSlide: (e: any) => void;
  width: number;
  height: number;
  index: number;
}) => {
  const { height, index, setInitialSlide, source, width } = props;
  return (
    <React.Fragment>
      {source.kind === "video" ? (
        <div
          onClick={() => setInitialSlide(index)}
          className={styles.cardWrapper}
          style={{ width: width, height: height }}
        >
          <div
            className={styles.cardContent}
            style={{
              backgroundImage: `url(${source.media})`,
            }}
          >
            <PlayButton className={styles.playButton} />
          </div>
        </div>
      ) : (
        <div
          onClick={() => setInitialSlide(index)}
          className={styles.cardWrapper}
          style={{ width: width, height: height }}
        >
          <div
            className={styles.cardContent}
            style={{
              backgroundImage: `url(${source.media})`,
            }}
          ></div>
        </div>
      )}
    </React.Fragment>
  );
};

const CarouselSlide = (props: {
  source: {
    kind: "image" | "video";
    thumbnail: string;
    media: string;
    wistiaId?: string;
    id?: any;
  };
}) => {
  const { source } = props;
  return (
    <>
      {source.kind === "video" ? (
        <div className={styles.sliderContainer}>
          <video className={styles.video} autoPlay controls>
            <source src={source.media} type="video/mp4" />
          </video>
        </div>
      ) : (
        <div className={styles.sliderContainer}>
          <img src={source.media} alt="" loading="lazy" />
        </div>
      )}
    </>
  );
};

interface Props {
  items: {
    kind: "image" | "video";
    thumbnail: string;
    media: string;
    wistiaId?: string;
    id?: any;
  }[];
  width?: number;
  height?: number;
  onClick?: (id: any, type: any) => void;
}
